import React, { useEffect, useState } from "react";
import { Download } from "lucide-react";
import api from "@/lib/api";

export default function AdminReportsPage() {
  const [overview, setOverview] = useState(null);
  const [growth, setGrowth] = useState({});
  const [searchTerms, setSearchTerms] = useState([]);
  const [conversion, setConversion] = useState(null);
  const [days, setDays] = useState(30);
  const [zeroOnly, setZeroOnly] = useState(false);

  useEffect(() => {
    api.get("/admin/reports/overview").then((r) => setOverview(r.data)).catch(() => {});
    api.get("/admin/reports/conversion").then((r) => setConversion(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/admin/reports/growth", { params: { days } }).then((r) => setGrowth(r.data)).catch(() => {});
  }, [days]);
  useEffect(() => {
    api.get("/admin/reports/search-terms", { params: { zero_only: zeroOnly, top: 30 } }).then((r) => setSearchTerms(r.data || [])).catch(() => setSearchTerms([]));
  }, [zeroOnly]);

  const exportCsv = () => {
    const token = window.localStorage.getItem("gcw_bearer") || "";
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/export.csv`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((b) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = "marketplace-report.csv";
        document.body.appendChild(a); a.click(); a.remove();
      });
  };

  return (
    <div className="max-w-6xl" data-testid="admin-reports">
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · Reports</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Marketplace analytics</h1>
          <p className="text-[#252A34]/70 text-sm mt-1">Live aggregates. Drill-downs (per-category / per-provider) land in a follow-up.</p>
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium" data-testid="reports-export">
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      {overview && (
        <section className="mb-8">
          <h2 className="font-display text-lg font-semibold text-[#071b22] mb-3">Snapshot</h2>
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
            {Object.entries({ ...overview.counts, ...overview.financials }).map(([k, v]) => (
              <div key={k} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`report-metric-${k}`}>
                <div className="text-xs uppercase tracking-wider text-[#252A34]/60">{k.replace(/_/g, " ")}</div>
                <div className="mt-1 font-display text-xl font-semibold text-[#071b22]">
                  {typeof v === "number" ? (k.includes("gmv") || k.includes("revenue") || k.includes("refund") ? `$${v.toFixed(2)}` : v.toLocaleString()) : String(v)}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mb-8">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <h2 className="font-display text-lg font-semibold text-[#071b22]">Growth</h2>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-full border border-slate-200 px-3 py-1.5 text-sm bg-white" data-testid="growth-days">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {["users", "providers", "bookings", "orders"].map((key) => (
            <div key={key} className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="text-xs uppercase tracking-wider text-[#252A34]/60">{key}</div>
              <div className="mt-1 font-display text-xl font-semibold text-[#071b22]">
                {(growth[key] || []).reduce((s, r) => s + (r.count || 0), 0).toLocaleString()}
              </div>
              <div className="mt-2 h-16 flex items-end gap-0.5">
                {(growth[key] || []).slice(-14).map((r) => {
                  const max = Math.max(1, ...(growth[key] || []).map((x) => x.count || 0));
                  const h = Math.max(2, Math.round(((r.count || 0) / max) * 60));
                  return <div key={r._id} className="flex-1 bg-[#165a71]/70 rounded-sm" style={{ height: `${h}px` }} title={`${r._id}: ${r.count}`} />;
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {conversion && (
        <section className="mb-8">
          <h2 className="font-display text-lg font-semibold text-[#071b22] mb-3">Conversion</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-xs uppercase tracking-wider text-[#252A34]/60">Bookings advanced (confirmed+)</div>
              <div className="mt-2 font-display text-2xl font-semibold text-[#071b22]">{(conversion.bookings.rate * 100).toFixed(1)}%</div>
              <div className="text-xs text-[#252A34]/60 mt-1">{conversion.bookings.advanced} of {conversion.bookings.total} · {conversion.bookings.canceled} canceled</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="text-xs uppercase tracking-wider text-[#252A34]/60">Orders completed</div>
              <div className="mt-2 font-display text-2xl font-semibold text-[#071b22]">{(conversion.orders.rate * 100).toFixed(1)}%</div>
              <div className="text-xs text-[#252A34]/60 mt-1">{conversion.orders.completed} of {conversion.orders.total}</div>
            </div>
          </div>
        </section>
      )}

      <section className="mb-8">
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <h2 className="font-display text-lg font-semibold text-[#071b22]">Search terms</h2>
          <label className="inline-flex items-center gap-2 text-xs text-[#252A34]">
            <input type="checkbox" checked={zeroOnly} onChange={(e) => setZeroOnly(e.target.checked)} data-testid="reports-zero-only" />
            Zero-result only
          </label>
        </div>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#252A34]/60 bg-slate-50">
                <th className="px-3 py-2">Query</th>
                <th className="px-3 py-2">Times searched</th>
                <th className="px-3 py-2">Avg results</th>
              </tr>
            </thead>
            <tbody>
              {searchTerms.length === 0 ? (
                <tr><td colSpan={3} className="p-6 text-center text-[#252A34]/60">No search terms yet.</td></tr>
              ) : searchTerms.map((r) => (
                <tr key={r._id} className="border-t border-slate-100" data-testid={`reports-search-${r._id}`}>
                  <td className="px-3 py-2 text-[#071b22]">{r._id}</td>
                  <td className="px-3 py-2">{r.count}</td>
                  <td className="px-3 py-2">{(r.avg_results || 0).toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
